import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Search, Plus, Eye, Play, Pause, Square, Trash2 } from "lucide-react";
import api from '@/lib/api';
import { toast } from "@/hooks/use-toast";

interface Cliente {
  id: string;
  nome: string;
  email: string;
  integracao_ativa: boolean;
}

interface TipoBoleto {
  codigo: string;
  descricao: string;
}

interface SituacaoVeiculo {
  codigo: string;
  descricao: string;
}

interface FiltrosBusca {
  tipos_boleto?: string[];
  situacoes_veiculo?: string[];
  periodo_inicio: string;
  periodo_fim: string;
}

interface BoletoHinova {
  nosso_numero: number;
  linha_digitavel: string;
  valor_boleto: string;
  valor_pagamento: string;
  codigo_tipo_boleto: string;
  tipo_boleto: string;
  codigo_associado: number;
  nome_associado: string;
  cpf: string;
  telefone_fixo: string;
  celular: string;
  telefone_comercial: string;
  email: string;
  email_auxiliar: string;
  data_inicio_contrato_associado: string;
  data_fim_contrato_associado: string;
  data_emissao: string;
  data_vencimento: string;
  data_vencimento_original: string;
  data_pagamento: string;
  codigo_situacao_boleto: string;
  situacao_boleto: string;
  codigo_banco: string;
  mes_referente: string;
  codigo_mgfformapagamento: string;
  veiculos: Array<{
    codigo_veiculo: number;
    placa: string;
    chassi: string;
    modelo: string;
    marca: string;
    situacao_veiculo: string;
  }>;
}

interface CampanhaIntegrada {
  id: string;
  cliente_id: string;
  nome: string;
  filtros: FiltrosBusca;
  dados_boletos: BoletoHinova[];
  status: 'preview' | 'ativa' | 'pausada' | 'cancelada';
  estatisticas: {
    total_registros: number;
    valor_total: number;
    enviados: number;
    pendentes: number;
    invalidos: number;
    baixados: number;
  };
  created_at: string;
  updated_at: string;
}

export default function CampanhasIntegradas() {
  const [activeTab, setActiveTab] = useState<'nova' | 'gerenciar'>('nova');
  
  // Estados para nova campanha
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [clienteSelecionado, setClienteSelecionado] = useState<string>('');
  const [tiposBoleto, setTiposBoleto] = useState<TipoBoleto[]>([]);
  const [situacoesVeiculo, setSituacoesVeiculo] = useState<SituacaoVeiculo[]>([]);
  const [filtros, setFiltros] = useState<FiltrosBusca>({
    periodo_inicio: '',
    periodo_fim: '',
    tipos_boleto: [],
    situacoes_veiculo: []
  });
  const [dadosPreview, setDadosPreview] = useState<{
    boletos: BoletoHinova[];
    estatisticas: any;
    total_registros: string;
  } | null>(null);
  const [nomeCampanha, setNomeCampanha] = useState('');
  
  // Estados para gerenciamento
  const [campanhas, setCampanhas] = useState<CampanhaIntegrada[]>([]);
  const [campanhaDetalhes, setCampanhaDetalhes] = useState<CampanhaIntegrada | null>(null);
  
  // Estados de loading
  const [loading, setLoading] = useState({
    clientes: false,
    filtros: false,
    busca: false,
    criacao: false,
    campanhas: false
  });

  // Carregar clientes integrados
  useEffect(() => {
    carregarClientesIntegrados();
    carregarCampanhas();
  }, []);

  // Carregar filtros quando cliente for selecionado
  useEffect(() => {
    if (clienteSelecionado) {
      carregarFiltros();
    }
  }, [clienteSelecionado]);

  const carregarClientesIntegrados = async () => {
    setLoading(prev => ({ ...prev, clientes: true }));
    try {
      const response = await api.get('/campanhas-integradas/clientes-integrados');
      setClientes(response.data);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao carregar clientes integrados",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, clientes: false }));
    }
  };

  const carregarFiltros = async () => {
    setLoading(prev => ({ ...prev, filtros: true }));
    try {
      const [tiposResponse, situacoesResponse] = await Promise.all([
        api.get(`/campanhas-integradas/tipos-boleto/${clienteSelecionado}`),
        api.get(`/campanhas-integradas/situacoes-veiculo/${clienteSelecionado}`)
      ]);
      
      setTiposBoleto(tiposResponse.data);
      setSituacoesVeiculo(situacoesResponse.data);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao carregar filtros da Hinova",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, filtros: false }));
    }
  };

  const buscarDadosHinova = async () => {
    if (!clienteSelecionado || !filtros.periodo_inicio || !filtros.periodo_fim) {
      toast({
        title: "Erro",
        description: "Selecione um cliente e defina o período",
        variant: "destructive"
      });
      return;
    }

    setLoading(prev => ({ ...prev, busca: true }));
    try {
      const response = await api.post(`/campanhas-integradas/buscar-dados/${clienteSelecionado}`, {
        filtros
      });
      
      setDadosPreview(response.data);
      toast({
        title: "Sucesso",
        description: `Encontrados ${response.data.total_registros} boletos`,
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao buscar dados da Hinova",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, busca: false }));
    }
  };

  const criarCampanha = async () => {
    if (!nomeCampanha || !dadosPreview) {
      toast({
        title: "Erro",
        description: "Nome da campanha e dados são obrigatórios",
        variant: "destructive"
      });
      return;
    }

    setLoading(prev => ({ ...prev, criacao: true }));
    try {
      await api.post('/campanhas-integradas/campanhas', {
        cliente_id: clienteSelecionado,
        nome: nomeCampanha,
        filtros,
        dados_boletos: dadosPreview.boletos
      });

      toast({
        title: "Sucesso",
        description: "Campanha criada com sucesso",
      });

      // Reset form
      setNomeCampanha('');
      setDadosPreview(null);
      setFiltros({
        periodo_inicio: '',
        periodo_fim: '',
        tipos_boleto: [],
        situacoes_veiculo: []
      });
      
      // Recarregar campanhas e mudar para aba de gerenciamento
      carregarCampanhas();
      setActiveTab('gerenciar');
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao criar campanha",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, criacao: false }));
    }
  };

  const carregarCampanhas = async () => {
    setLoading(prev => ({ ...prev, campanhas: true }));
    try {
      const response = await api.get('/campanhas-integradas/campanhas');
      setCampanhas(response.data);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao carregar campanhas",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, campanhas: false }));
    }
  };

  const atualizarStatusCampanha = async (id: string, status: string) => {
    try {
      await api.patch(`/campanhas-integradas/campanhas/${id}/status`, { status });
      toast({
        title: "Sucesso",
        description: "Status da campanha atualizado",
      });
      carregarCampanhas();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao atualizar status da campanha",
        variant: "destructive"
      });
    }
  };

  const deletarCampanha = async (id: string) => {
    try {
      await api.delete(`/campanhas-integradas/campanhas/${id}`);
      toast({
        title: "Sucesso",
        description: "Campanha deletada com sucesso",
      });
      carregarCampanhas();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao deletar campanha",
        variant: "destructive"
      });
    }
  };

  const formatarValor = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      preview: { label: 'Preview', variant: 'secondary' as const },
      ativa: { label: 'Ativa', variant: 'default' as const },
      pausada: { label: 'Pausada', variant: 'outline' as const },
      cancelada: { label: 'Cancelada', variant: 'destructive' as const }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Campanhas Integradas</h1>
        <p className="text-muted-foreground">
          Gerencie campanhas integradas com dados da Hinova
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'nova' | 'gerenciar')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="nova">Nova Campanha</TabsTrigger>
          <TabsTrigger value="gerenciar">Gerenciar Campanhas</TabsTrigger>
        </TabsList>

        <TabsContent value="nova" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Criar Nova Campanha</CardTitle>
              <CardDescription>
                Selecione um cliente e configure os filtros para buscar dados da Hinova
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Seleção de Cliente */}
              <div className="space-y-2">
                <Label htmlFor="cliente">Cliente Integrado</Label>
                <Select
                  value={clienteSelecionado}
                  onValueChange={setClienteSelecionado}
                  disabled={loading.clientes}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um cliente com integração ativa" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.filter(cliente => cliente.id && String(cliente.id).trim() !== '').map((cliente) => (
                      <SelectItem key={cliente.id} value={String(cliente.id)}>
                        {cliente.nome} ({cliente.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {clienteSelecionado && (
                <>
                  {/* Período */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="periodo_inicio">Data Início</Label>
                      <Input
                        id="periodo_inicio"
                        type="date"
                        value={filtros.periodo_inicio}
                        onChange={(e) => setFiltros(prev => ({ ...prev, periodo_inicio: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="periodo_fim">Data Fim</Label>
                      <Input
                        id="periodo_fim"
                        type="date"
                        value={filtros.periodo_fim}
                        onChange={(e) => setFiltros(prev => ({ ...prev, periodo_fim: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Filtros */}
                  {loading.filtros ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="ml-2">Carregando filtros...</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Tipos de Boleto</Label>
                        <MultiSelect
                          options={tiposBoleto.map(tipo => ({
                            label: tipo.descricao,
                            value: tipo.codigo
                          }))}
                          onValueChange={(values) => setFiltros(prev => ({ ...prev, tipos_boleto: values }))}
                          placeholder="Selecione os tipos de boleto"
                          variant="inverted"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Situações do Veículo</Label>
                        <MultiSelect
                          options={situacoesVeiculo.map(situacao => ({
                            label: situacao.descricao,
                            value: situacao.codigo
                          }))}
                          onValueChange={(values) => setFiltros(prev => ({ ...prev, situacoes_veiculo: values }))}
                          placeholder="Selecione as situações do veículo"
                          variant="inverted"
                        />
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={buscarDadosHinova}
                    disabled={loading.busca || !filtros.periodo_inicio || !filtros.periodo_fim}
                    className="w-full"
                  >
                    {loading.busca && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <Search className="mr-2 h-4 w-4" />
                    Buscar Dados
                  </Button>
                </>
              )}

              {/* Preview dos Dados */}
              {dadosPreview && (
                <Card>
                  <CardHeader>
                    <CardTitle>Preview dos Dados</CardTitle>
                    <CardDescription>
                      Dados encontrados na Hinova com os filtros aplicados
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                          {dadosPreview.total_registros}
                        </div>
                        <div className="text-sm text-blue-600">Total de Boletos</div>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                          {formatarValor(dadosPreview.estatisticas.valor_total)}
                        </div>
                        <div className="text-sm text-green-600">Valor Total</div>
                      </div>
                      <div className="text-center p-3 bg-yellow-50 rounded-lg">
                        <div className="text-2xl font-bold text-yellow-600">
                          {dadosPreview.estatisticas.pendentes}
                        </div>
                        <div className="text-sm text-yellow-600">Pendentes</div>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-600">
                          {dadosPreview.estatisticas.enviados}
                        </div>
                        <div className="text-sm text-gray-600">Enviados</div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="nome_campanha">Nome da Campanha</Label>
                        <Input
                          id="nome_campanha"
                          value={nomeCampanha}
                          onChange={(e) => setNomeCampanha(e.target.value)}
                          placeholder="Digite o nome da campanha"
                        />
                      </div>

                      <Button
                        onClick={criarCampanha}
                        disabled={loading.criacao || !nomeCampanha}
                        className="w-full"
                      >
                        {loading.criacao && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        <Plus className="mr-2 h-4 w-4" />
                        Criar Campanha
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="gerenciar" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Gerenciador de Campanhas Integradas</CardTitle>
              <CardDescription>
                Visualize e gerencie suas campanhas integradas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading.campanhas ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <span className="ml-2">Carregando campanhas...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Período</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Criada em</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campanhas.map((campanha) => (
                      <TableRow key={campanha.id}>
                        <TableCell className="font-medium">{campanha.nome}</TableCell>
                        <TableCell>{campanha.cliente_id}</TableCell>
                        <TableCell>
                          {formatarData(campanha.filtros.periodo_inicio)} - {formatarData(campanha.filtros.periodo_fim)}
                        </TableCell>
                        <TableCell>{campanha.estatisticas.total_registros}</TableCell>
                        <TableCell>{formatarValor(campanha.estatisticas.valor_total)}</TableCell>
                        <TableCell>{getStatusBadge(campanha.status)}</TableCell>
                        <TableCell>{formatarData(campanha.created_at)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setCampanhaDetalhes(campanha)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-4xl">
                                <DialogHeader>
                                  <DialogTitle>Detalhes da Campanha</DialogTitle>
                                  <DialogDescription>
                                    Informações detalhadas da campanha {campanha.nome}
                                  </DialogDescription>
                                </DialogHeader>
                                {/* Conteúdo do modal será implementado */}
                              </DialogContent>
                            </Dialog>
                            
                            {campanha.status === 'preview' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => atualizarStatusCampanha(campanha.id, 'ativa')}
                              >
                                <Play className="h-4 w-4" />
                              </Button>
                            )}
                            
                            {campanha.status === 'ativa' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => atualizarStatusCampanha(campanha.id, 'pausada')}
                              >
                                <Pause className="h-4 w-4" />
                              </Button>
                            )}
                            
                            {campanha.status === 'pausada' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => atualizarStatusCampanha(campanha.id, 'ativa')}
                              >
                                <Play className="h-4 w-4" />
                              </Button>
                            )}
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => atualizarStatusCampanha(campanha.id, 'cancelada')}
                            >
                              <Square className="h-4 w-4" />
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deletarCampanha(campanha.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
